import { useDraft } from './DraftContext';
import { useIsUwView } from '../components/uwTileSpacing';

/**
 * The coverage grid's column widths, in one place, because the Proposed column
 * changes them.
 *
 * The grid is twelve units and was full: 4 name + 2 limit + 5 inputs + 1
 * premium. Name took a unit from limit so long coverage names read in full;
 * the slider stays usable at two. A Proposed column has to come out of one of those, and the header
 * and every row have to agree about which — they hardcoded the same four
 * numbers in two files, and a column that appears and disappears turns that
 * duplication from harmless into a misaligned grid the first time anything
 * stages.
 *
 * The unit comes from the inputs block. It is the widest and the only one
 * holding more than one thing, so it is the only one that can give a unit up
 * without a column losing its label; name, limit and premium are each already
 * at their content's width.
 */
export interface CoverageColumnSpans {
  readonly name: number;
  readonly limit: number;
  /** Retention, plus the optional territorial / basis / waiting / retro cells. */
  readonly inputs: number;
  readonly premium: number;
  /** Zero when the column is not showing, which is also "do not render it". */
  readonly proposed: number;
  /**
   * The accept/reject pair, in a column of its own with no heading.
   *
   * It used to share the Proposed cell, right-aligned beside the figure — which
   * pushed the proposed premium left of the "Proposed" heading above it, so the
   * heading sat over the icons rather than over the number it names. A heading
   * belongs to one column; the pair is not part of what it names.
   *
   * Zero when nothing is staged, which is also "do not render it".
   */
  readonly actions: number;
}

export function coverageColumnSpans(
  showsProposed: boolean
): CoverageColumnSpans {
  if (!showsProposed) {
    return {
      name: 4,
      limit: 2,
      inputs: 5,
      premium: 1,
      proposed: 0,
      actions: 0,
    };
  }
  // Both new columns come out of the inputs block, for the reason above it: it
  // is the only one holding more than one thing, so it is the only one that can
  // give a unit up without a column losing its label.
  // Three-quarters of a unit each: the proposed figure and the accept/reject
  // pair are each under ~90px, and a whole unit apiece squeezed the inputs.
  return {
    name: 4,
    limit: 2,
    inputs: 3.5,
    premium: 1,
    proposed: 0.75,
    actions: 0.75,
  };
}

/**
 * Whether the grid is showing a Proposed column right now.
 *
 * Keyed on anything being staged, not on the rate having landed. The column has
 * to appear with the change rather than with its price — appearing a second
 * later, once the rater answers, would reflow the grid twice for one edit, the
 * second time under an underwriter already reading the new numbers.
 *
 * Outside a `DraftProvider` (midterm endorsements shares this tree) `useDraft`
 * resolves to the disabled default, so this is false and the grid is exactly
 * what it is today.
 */
export function useProposedColumn(): CoverageColumnSpans {
  const { counts } = useDraft();
  return coverageColumnSpans(counts.total > 0);
}

/**
 * The component (multi-rater) coverage grid's column widths.
 *
 * A different grid from the one above, with its own header
 * (`UwCoverageListingHeader`) and its own row
 * (`CoverageComponentLineItem`): no premium, no proposed premium, and a dead
 * cell between the coverage name and Limit that exists only to push the two
 * input columns under their headings.
 *
 * That dead cell is where the accept/reject column comes from. Name, Limit and
 * Retention are each at their content's width, so the spacer is the only thing
 * on the row with units to give — and giving them up moves Limit and Retention
 * left by two units in both the header and every row at once, which is why
 * both read this rather than restating it.
 */
export interface ComponentCoverageColumnSpans {
  readonly name: number;
  /** Dead space under the group title. The only column with width to give. */
  readonly spacer: number;
  readonly limit: number;
  /** The Retention heading; the field under it is the deductible. */
  readonly deductible: number;
  /** Zero when nothing is staged, which is also "do not render it". */
  readonly actions: number;
}

export function componentCoverageColumnSpans(
  showsActions: boolean,
  /**
   * The agency quote views render this grid's header over rows that hardcode
   * 3/3/3/3, so they keep exactly that; only the underwriter view widens the
   * name column.
   */
  isUwView = true
): ComponentCoverageColumnSpans {
  if (!isUwView) {
    // Develop's spans exactly, staged layout included.
    return showsActions
      ? { name: 3, spacer: 1, limit: 3, deductible: 3, actions: 2 }
      : { name: 3, spacer: 3, limit: 3, deductible: 3, actions: 0 };
  }
  if (!showsActions) {
    return { name: 4, spacer: 2, limit: 3, deductible: 3, actions: 0 };
  }
  // One unit is enough for the pair, as on the single-rater grid; the name
  // took the other from the spacer so long coverage names read in full.
  return { name: 4, spacer: 1, limit: 3, deductible: 3, actions: 1 };
}

/**
 * Whether this grid is showing an accept/reject column right now.
 *
 * Keyed on anything being staged anywhere on the quote, not on this row having
 * something staged: the column has to be the same width on every row of a grid
 * or the rows do not line up with each other, let alone with the header.
 */
export function useComponentCoverageColumns(): ComponentCoverageColumnSpans {
  const { counts } = useDraft();
  const isUwView = useIsUwView();
  return componentCoverageColumnSpans(counts.total > 0, isUwView);
}

/** The coverage grid's input columns, in the order they render. */
export type CoverageInputColumn =
  | 'retention'
  | 'territorialLimit'
  | 'basisOfCover'
  | 'waitingPeriod'
  | 'retroactive';

interface CoverageInputColumnSettings {
  readonly showCoverageTerritorialLimit?: boolean;
  readonly showCoverageBasisOfCover?: boolean;
  readonly showCoverageWaitingPeriods?: boolean;
  readonly showCoverageRetroactiveDate?: boolean;
}

/**
 * Which input columns the coverage grid shows, left to right. Retention is
 * always there; the rest follow the product's UI settings. The panel header
 * labels these. The row cells read the same four flags.
 */
export function coverageInputColumns(
  settings: CoverageInputColumnSettings
): CoverageInputColumn[] {
  const columns: CoverageInputColumn[] = ['retention'];
  if (settings.showCoverageTerritorialLimit) columns.push('territorialLimit');
  if (settings.showCoverageBasisOfCover) columns.push('basisOfCover');
  if (settings.showCoverageWaitingPeriods) columns.push('waitingPeriod');
  if (settings.showCoverageRetroactiveDate) columns.push('retroactive');
  return columns;
}
